import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { API_BASE_URL } from "../../../src/App";
import { useSelector } from "react-redux";
import { KeyRound, Lock, Eye, EyeOff, Loader2, CheckCircle, ArrowLeft } from "lucide-react";

export default function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const contact: string = (location.state as any)?.contact || ""; // Can be mobile or email

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | "">("");

  const { username = "", verifyOtpToken = "" } = useSelector((state: any) => state.loginData || {})

  // Calculate password strength
  const calculateStrength = (password: string) => {
    if (!password) {
      setPasswordStrength("");
      return;
    }
    if (password.length < 4) {
      setPasswordStrength("weak");
    } else if (password.length < 8 || !/[^a-zA-Z0-9]/.test(password)) {
      setPasswordStrength("medium");
    } else {
      setPasswordStrength("strong");
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    calculateStrength(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError("Both fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 4) {
      setError("Password must be at least 4 characters long");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // Determine if contact is email or mobile
      const isEmail = contact.includes("@");
      const payload = isEmail 
        ? { email: contact, mobile: "", username: "", password: newPassword }
        : { mobile: contact, email: "", username: "", password: newPassword };
      
      const resp = await axios.post(
        `${API_BASE_URL}newPassword`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${verifyOtpToken}`
          }
        }
      );
      
      setSuccess(true);
      
      // Navigate after a brief delay
      setTimeout(() => {
        navigate("/panel");
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const isFormValid = passwordsMatch && newPassword.length >= 4;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-100 via-blue-200 to-sky-300 p-4">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-500/15 to-sky-300/15 rounded-full blur-3xl -z-10 hidden md:block"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-blue-500/15 to-sky-300/15 rounded-full blur-3xl -z-10 hidden md:block"></div>

      <Card className="w-full max-w-md shadow-2xl border border-slate-200/60 hover:shadow-3xl transition-all duration-300">
        {/* Header with branding */}
        <div className="relative bg-gradient-to-br from-blue-950 via-blue-800 to-sky-600 p-8 rounded-t-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 right-2 w-20 h-20 bg-blue-300/20 rounded-full blur-xl"></div>
            <div className="absolute bottom-2 left-2 w-16 h-16 bg-blue-300/20 rounded-full blur-xl"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-300 via-blue-500 to-sky-600 flex items-center justify-center shadow-lg shadow-blue-500/50 hover:shadow-blue-600/70 transition-all hover:scale-110">
                <KeyRound className="text-white w-8 h-8" strokeWidth={1.5} />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">Reset Password</CardTitle>
            <CardDescription className="text-emerald-200 text-base">
              Create a strong new password for your account
            </CardDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5 pt-8 pb-6">
            {/* Error Alert */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200/60 rounded-xl flex gap-3 animate-shake">
                <div className="w-5 h-5 rounded-full bg-red-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Success Alert */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200/60 rounded-xl flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-green-700 text-sm font-medium">Password reset successful! Redirecting...</p>
              </div>
            )}

            {/* New Password Field */}
            <div className="space-y-2.5">
              <Label htmlFor="newPassword" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Lock size={16} className="text-emerald-200" />
                New Password
              </Label>
              <div className="relative group">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading || success}
                  className="pl-4 pr-12 py-3 border-2 border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm transition-all focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10 focus:ring-0 placeholder:text-slate-400 disabled:opacity-50 disabled:bg-slate-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading || success}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="space-y-2">
                  <div className="flex gap-1.5">
                    {Array(3).fill(0).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all ${
                          passwordStrength === "weak" ? (i === 0 ? "bg-red-400" : "bg-slate-200") :
                          passwordStrength === "medium" ? (i <= 1 ? "bg-yellow-400" : "bg-slate-200") :
                          passwordStrength === "strong" ? "bg-green-400" : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${
                    passwordStrength === "weak" ? "text-red-600" :
                    passwordStrength === "medium" ? "text-yellow-600" :
                    "text-green-600"
                  }`}>
                    {passwordStrength === "weak" ? "Weak password" :
                     passwordStrength === "medium" ? "Medium strength" :
                     "Strong password"}
                  </p>
                </div>
              )}

              <p className="text-xs text-slate-500">
                At least 8 characters recommended
              </p>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2.5">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Lock size={16} className="text-emerald-200" />
                Confirm Password
              </Label>
              <div className="relative group">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={loading || success}
                  className="pl-4 pr-12 py-3 border-2 border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm transition-all focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10 focus:ring-0 placeholder:text-slate-400 disabled:opacity-50 disabled:bg-slate-50"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading || success}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Match indicator */}
              {confirmPassword && (
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordsMatch ? "bg-green-100" : "bg-red-100"}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${passwordsMatch ? "bg-green-500" : "bg-red-500"}`}></div>
                  </div>
                  <p className={`text-xs font-medium ${passwordsMatch ? "text-green-600" : "text-red-600"}`}>
                    {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                  </p>
                </div>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pb-8">
            <Button 
              type="submit" 
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 group active:scale-95"
              disabled={loading || success || !isFormValid}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Resetting...
                </>
              ) : success ? (
                <>
                  <CheckCircle size={18} />
                  Password Reset!
                </>
              ) : (
                <>
                  <KeyRound size={18} className="group-hover:translate-x-1 transition-transform" />
                  Reset Password
                </>
              )}
            </Button>

            <button
              type="button"
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline decoration-emerald-200 transition-all p-2 hover:bg-emerald-50 rounded-lg w-full flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={() => navigate("/verifyotp")}
              disabled={loading || success}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </CardFooter>
        </form>

        {/* Footer info */}
        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/50">
          <p className="text-xs text-slate-500 text-center">
            Use a strong password with mixed characters for better security
          </p>
        </div>
      </Card>
    </div>
  );
}
